// ProfileMenu.tsx - With Framer Motion animations
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../context/TranslationProvider";
import { User, Settings, Upload, Palette, HelpCircle, LogOut, ChevronDown } from "lucide-react";

export interface ProfileMenuItem {
  label: string;
  to: string;
  icon?: React.ReactNode;
}

interface ProfileMenuProps {
  user: { email?: string; username?: string; profile_picture_url?: string };
  menuItems?: ProfileMenuItem[];
  logout: () => void;
  avatarUrl?: string;
  className?: string;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({
  user,
  menuItems,
  logout,
  avatarUrl,
  className = "",
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { direction } = useLang();
  const { t } = useLang();

  const defaultMenuItems: ProfileMenuItem[] = [
    { label: t("Profile"), to: "/myprofile", icon: <User className="w-4 h-4" /> },
    { label: t("Uploads"), to: "/app/uploads", icon: <Upload className="w-4 h-4" /> },
    { label: t("Settings"), to: "/app/settings", icon: <Settings className="w-4 h-4" /> },
    { label: t("Theme"), to: "/app/theme", icon: <Palette className="w-4 h-4" /> },
    { label: t("Support"), to: "/app/support", icon: <HelpCircle className="w-4 h-4" /> },
  ];

  const finalMenuItems = menuItems || defaultMenuItems;
  const displayName = user?.username || user?.email?.split('@')[0] || "User";
  const userEmail = user?.email;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setMenuOpen(!menuOpen)}
        className="flex items-center gap-2 rounded-full md:px-3 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        aria-label="Profile menu"
        aria-expanded={menuOpen}
      >
        <div className="hidden sm:flex flex-col items-end">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {displayName}
          </span>
          {userEmail && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {userEmail}
            </span>
          )}
        </div>
        
        <div className="relative">
          <div className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 ring-2 ring-gray-200 dark:ring-gray-700 hover:ring-purple-300 dark:hover:ring-purple-700 transition-all">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Avatar"
                className="w-full h-full object-cover"
              />
            ) : user?.profile_picture_url ? (
              <img
                src={user.profile_picture_url}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="font-bold text-gray-600 dark:text-gray-300 text-sm">
                {displayName.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <ChevronDown 
            className={`absolute -bottom-1 -right-1 w-3 h-3 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${
              menuOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </motion.button>

      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-30"
              onClick={() => setMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Dropdown menu */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className={`absolute ${
                direction === "rtl" ? "left-0" : "right-0"
              } z-50 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/50 overflow-hidden border border-gray-200 dark:border-gray-700`}
            >
              {/* User Info Section */}
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 flex items-center justify-center">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : user?.profile_picture_url ? (
                      <img
                        src={user.profile_picture_url}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-bold text-gray-600 dark:text-gray-300 text-base">
                        {displayName.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {displayName}
                    </p>
                    {userEmail && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {userEmail}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="flex flex-col py-1">
                {finalMenuItems.map((item, idx) => (
                  <Link
                    key={idx}
                    to={item.to}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150"
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className="text-gray-400 dark:text-gray-500">
                      {item.icon}
                    </span>
                    {item.label}
                  </Link>
                ))}

                <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>

                <button
                  onClick={() => {
                    logout();
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150 w-full text-left"
                >
                  <LogOut className="w-4 h-4" />
                  {t("Logout")}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfileMenu;